/**
 * HardwareRenderer — R3F component that renders all hardware for a single cabinet.
 *
 * Fetches hardware-3d data for the line item, loads GLB models,
 * computes placements via the placement engine, and renders instances + drill holes.
 */

import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { apiClient } from '@/api/client';
import { useHardwareModels } from './useHardwareModels';
import HardwareInstance from './HardwareInstance';
import DrillHoleRenderer from './DrillHoleRenderer';
import { computeHardwarePlacements } from '@/lib/hardware-placement/placementEngine';
import type { CabinetDimensions, MountingProfileData, DrillPointConfig } from '@/lib/hardware-placement/types';
import type { HardwarePlacement } from '@/lib/hardware-placement/types';
import type { CabinetData } from './types';

interface HardwareItem {
  hardwareId: string;
  hardwareName: string;
  modelDataUrl: string;
  mountingProfiles: MountingProfileData[];
  drillPointsJson: Record<string, DrillPointConfig>;
  scale: number;
}

interface HardwareRendererProps {
  lineItemId: string;
  cabinetData: CabinetData;
  showHardware: boolean;
  showDrillHoles: boolean;
}

export default function HardwareRenderer({
  lineItemId,
  cabinetData,
  showHardware,
  showDrillHoles,
}: HardwareRendererProps) {
  const { loadModel, getModel } = useHardwareModels();
  const [loadedModels, setLoadedModels] = useState<Record<string, GLTF | null>>({});

  // Fetch hardware-3d data for this cabinet
  const { data: hardwareItems } = useQuery<HardwareItem[]>({
    queryKey: ['cabinet-hardware-3d', lineItemId],
    queryFn: async () => {
      const res = await apiClient.get(`/calculations/cabinet/${lineItemId}/hardware-3d`);
      return res.data;
    },
    enabled: showHardware,
  });

  // Build cabinet dimensions for the placement engine
  const cabinetDimensions = useMemo<CabinetDimensions>(() => ({
    width: cabinetData.width,
    height: cabinetData.height,
    depth: cabinetData.depth,
    doorCount: cabinetData.num_doors,
    drawerCount: cabinetData.num_drawers,
    shelfCount: cabinetData.num_shelves,
    isLeftHinged: true, // default; could be derived from face_component_config
  }), [cabinetData]);

  // Load GLB models when hardware items arrive
  useEffect(() => {
    if (!hardwareItems || hardwareItems.length === 0) return;

    let cancelled = false;

    async function loadAll() {
      const results: Record<string, GLTF | null> = {};
      for (const item of hardwareItems!) {
        if (!item.modelDataUrl) continue;
        const gltf = await loadModel(item.hardwareId, item.modelDataUrl);
        if (cancelled) return;
        results[item.hardwareId] = gltf;
      }
      if (!cancelled) {
        setLoadedModels(results);
      }
    }

    loadAll();

    return () => {
      cancelled = true;
    };
  }, [hardwareItems, loadModel]);

  // Compute placements for each hardware item
  const hardwareRenderData = useMemo(() => {
    if (!hardwareItems) return [];

    return hardwareItems.map((item) => {
      const gltf = loadedModels[item.hardwareId] ?? getModel(item.hardwareId);
      const placements = computeHardwarePlacements(item.mountingProfiles, cabinetDimensions);

      return {
        item,
        gltf,
        placements,
      };
    });
  }, [hardwareItems, loadedModels, getModel, cabinetDimensions]);

  if (!showHardware || !hardwareItems || hardwareItems.length === 0) {
    return null;
  }

  return (
    <group>
      {hardwareRenderData.map(({ item, gltf, placements }) => (
        <group key={item.hardwareId}>
          {/* Render hardware instances */}
          {gltf && placements.map((placement: HardwarePlacement, idx: number) => (
            <HardwareInstance
              key={`${item.hardwareId}-inst-${idx}`}
              gltfScene={gltf.scene}
              placement={placement}
              scale={item.scale ?? 1}
            />
          ))}

          {/* Render drill holes */}
          {item.drillPointsJson && Object.keys(item.drillPointsJson).length > 0 && (
            <DrillHoleRenderer
              drillPoints={item.drillPointsJson}
              placements={placements}
              visible={showDrillHoles}
            />
          )}
        </group>
      ))}
    </group>
  );
}
